<template>
	<div class="survey">
		<div class="survey-container">
			<div style="text-align: right;">
        <img
          width="119"
          :src="$apiUrl + '/storage/P6wBgrhyARRdvwX32u5IFnPYtoIcp1GxXAFJ2dsp.png'"
          alt=""
        />
			</div>
			
			<div class="d-flex justify-space-between mb-7">
				<div>
					<h2 class="mr-5">Resan till och från skolan</h2>
					<p>– tack för att du bidrar!</p>
				</div>
			</div>

			<div v-if="stepIndex === 0">
				<p>I denna korta enkät (cirka 2 minuter) får du svara på hur {{ $processText('{survey-into-text-adaptation}') }} resande till och från skolan ser ut idag. Vi använder svaren till att personanpassa informationen om aktivt resande som du sen kommer att få tillgång till. Vi använder också svaren för att utvärdera projektet. Läs mer om hur <a :href="$processText('https://data.nudgd.se/{slug}')" target="_blank">dina personuppgifter hanteras.</a>.</p>
				<p>Vi ställer inga känsliga frågor och vi behandlar och förvarar din svarsdata säkert och anonymiserat. Utöver nu på hösten 2023 kan den komma att användas även i framtida analyser. Enkäten hanteras av företaget Nudgd på uppdrag av Helsingborgs stad.</p>

				<h2 class="mt-10 mb-2">Färdmedel till och från skolan</h2>
				<p class="mb-0"><em class="smaller-em">Fyll i det alternativ som passar bäst respektive uppskattningar som känns rimliga.</em></p>
				
				<h4 class="mt-3 mb-3" :class="{surveyError: errors.includes('mainModeOfTransport')}">{{ $processText('{survey-main-travel-mode}') }}</h4>
				<ModeOfTransport :key="1" :value="mainModeOfTransport" @selected="selectModeOfTransport" />

				<div class="nudgd-btn inline survey-submit mt-5" @click="confirmFirstStep()" v-if=" ! firstStepConfirmed">Gå vidare</div>
				<div style="clear:both;"></div>

				<template v-if="firstStepConfirmed">

					<h4 class="mt-2 mb-3">{{ $processText('{survey-travel-mode-days-per-week}') }}</h4>
					<p class="mb-2"><em class="smaller-em">(Används olika färdmedel för samma resa, räkna med alla)</em></p>
					<div class="survey-frequency-chips walk">
						<h3 :class="{surveyError: errors.includes('frequencyWalk')}">🚶 Till fots</h3>
						<div class="survey-frequency-chips-container">
							<div class="survey-frequency-chips-container-chip walking" :class="{active: frequencyWalk == number}" v-for="number in [0, 1, 2, 3, 4, 5]" :key="number" @click="selectFrequency('frequencyWalk', number)">{{ number }}</div>
						</div>
						<div class="survey-frequency-chips-helpers right">
							<div>dagar per vecka</div>
						</div>
					</div>
					<div class="survey-frequency-chips">
						<h3 :class="{surveyError: errors.includes('frequencyBike')}">🚲 Cykel/lådcykel</h3>
						<div class="survey-frequency-chips-container">
							<div class="survey-frequency-chips-container-chip bike" :class="{active: frequencyBike == number}" v-for="number in [0, 1, 2, 3, 4, 5]" :key="number" @click="selectFrequency('frequencyBike', number)">{{ number }}</div>
						</div>
						<div class="survey-frequency-chips-helpers right">
							<div>dagar per vecka</div>
						</div>
					</div>
					<div class="survey-frequency-chips">
						<h3 :class="{surveyError: errors.includes('frequencyTransit')}">🚌 Kollektivt</h3>
						<div class="survey-frequency-chips-container">
							<div class="survey-frequency-chips-container-chip transit" :class="{active: frequencyTransit == number}" v-for="number in [0, 1, 2, 3, 4, 5]" :key="number" @click="selectFrequency('frequencyTransit', number)">{{ number }}</div>
						</div>
						<div class="survey-frequency-chips-helpers right">
							<div>dagar per vecka</div>
						</div>
					</div>
					<div class="survey-frequency-chips">
						<h3 :class="{surveyError: errors.includes('frequencyCar')}">{{ $processText('{survey-travel-mode-days-per-week-car-own}') }}</h3>
						<div class="survey-frequency-chips-container">
							<div class="survey-frequency-chips-container-chip car" :class="{active: frequencyCar == number}" v-for="number in [0, 1, 2, 3, 4, 5]" :key="number" @click="selectFrequency('frequencyCar', number)">{{ number }}</div>
						</div>
						<div class="survey-frequency-chips-helpers right">
							<div>dagar per vecka</div>
						</div>
					</div>
					<div class="survey-frequency-chips">
						<h3 :class="{surveyError: errors.includes('frequencyCarCoride')}">🚙 Bil – samåkning med andra</h3>
						<div class="survey-frequency-chips-container">
							<div class="survey-frequency-chips-container-chip car" :class="{active: frequencyCarCoride == number}" v-for="number in [0, 1, 2, 3, 4, 5]" :key="number" @click="selectFrequency('frequencyCarCoride', number)">{{ number }}</div>
						</div>
						<div class="survey-frequency-chips-helpers right">
							<div>dagar per vecka</div>
						</div>
					</div>
					<div class="survey-frequency-chips">
						<h3 :class="{surveyError: errors.includes('frequencyMisc')}">Övrigt</h3>
						<div class="survey-frequency-chips-container">
							<div class="survey-frequency-chips-container-chip" :class="{active: frequencyMisc == number}" v-for="number in [0, 1, 2, 3, 4, 5]" :key="number" @click="selectFrequency('frequencyMisc', number)">{{ number }}</div>
						</div>
						<div class="survey-frequency-chips-helpers right">
							<div>dagar per vecka</div>
						</div>
					</div>

					<template v-if="frequencyCar || frequencyMisc || frequencyWalk || frequencyTransit || frequencyCarCoride || frequencyBike">
						<h4 class="mt-2 mb-1">{{ $processText('{survey-average-number-weekly-trips}') }}</h4>
						<p class="mb-2"><em class="smaller-em">(Tur och retur räknas som två enkelresor. Exempel: Två barn som färdas till och från skolan fem dagar i veckan blir 20 enkelresor per vecka.)</em></p>

						<div class="survey-thirds mt-3">
							<div class="survey-thirds-first" :class="{surveyError: errors.includes('numberOfTrips')}">Antal enkelresor per vecka:</div>

							<div class="survey-thirds-middle">
								<vue-slider dotSize="20" :max="35" v-model="numberOfTrips"></vue-slider>
							</div>

							<div class="survey-thirds-last"><v-text-field placeholder="0" v-model="numberOfTrips"></v-text-field> resor</div>
						</div>

						<h4 class="mt-6 mb-6">{{ $processText('{survey-average-weekly-distance-per-travel-mode}') }}</h4>
						<p class="mb-2"><em class="smaller-em">(Räkna med både vägen till och från skolan/skolorna, inklusive eventuella ärenden på vägen. Du kan antingen dra i ikonen eller skriva in avståndet direkt i kolumnen till höger.)</em></p>
						<div class="survey-thirds walking mt-3" v-if="frequencyWalk > 0">
							<div class="survey-thirds-first-alt" :class="{surveyError: errors.includes('weeklyWalking')}">Till fots</div>
							<div class="survey-thirds-middle"><vue-slider :max="200" v-model="weeklyWalking"><template v-slot:dot><div class="custom-dot alt">🚶</div></template></vue-slider></div>
							<div class="survey-thirds-last"><v-text-field placeholder="0" v-model="weeklyWalking"></v-text-field> km</div>
						</div>

						<div class="survey-thirds bike mt-3" v-if="frequencyBike > 0">
							<div class="survey-thirds-first-alt" :class="{surveyError: errors.includes('weeklyBike')}">Cykel/lådcykel</div>
							<div class="survey-thirds-middle"><vue-slider :max="200" v-model="weeklyBike"><template v-slot:dot><div class="custom-dot alt">🚲</div></template></vue-slider></div>
							<div class="survey-thirds-last"><v-text-field placeholder="0" v-model="weeklyBike"></v-text-field> km</div>
						</div>

						<div class="survey-thirds transit mt-3" v-if="frequencyTransit > 0">
							<div class="survey-thirds-first-alt" :class="{surveyError: errors.includes('weeklyTransit')}">Kollektivt</div>
							<div class="survey-thirds-middle"><vue-slider :max="1000" v-model="weeklyTransit"><template v-slot:dot><div class="custom-dot alt">🚌</div></template></vue-slider></div>
							<div class="survey-thirds-last"><v-text-field placeholder="0" v-model="weeklyTransit"></v-text-field> km</div>
						</div>

						<div class="survey-thirds car mt-3" v-if="frequencyCar > 0">
							<div class="survey-thirds-first-alt" :class="{surveyError: errors.includes('weeklyCar')}">Bil - själv eller med hushållsmedlem</div>
							<div class="survey-thirds-middle"><vue-slider :max="1000" v-model="weeklyCar"><template v-slot:dot><div class="custom-dot alt">🚗</div></template></vue-slider></div>
							<div class="survey-thirds-last"><v-text-field placeholder="0" v-model="weeklyCar"></v-text-field> km</div>
						</div>

						<div class="survey-thirds car carCoride mt-3" v-if="frequencyCarCoride > 0">
							<div class="survey-thirds-first-alt" :class="{surveyError: errors.includes('weeklyCarCoride')}">Bil - samåkning med annan</div>
							<div class="survey-thirds-middle"><vue-slider :max="1000" v-model="weeklyCarCoride"><template v-slot:dot><div class="custom-dot alt">🚙</div></template></vue-slider></div>
							<div class="survey-thirds-last"><v-text-field placeholder="0" v-model="weeklyCarCoride"></v-text-field> km</div>
						</div>

						<div class="survey-thirds misc mt-3" v-if="frequencyMisc > 0">
							<div class="survey-thirds-first-alt" :class="{surveyError: errors.includes('weeklyMisc')}">
								<v-tooltip bottom>
							      <template v-slot:activator="{ on, attrs }"><span v-bind="attrs" v-on="on">Övrigt <div class="tooltip-container"><span class="icon-info"></span></div></span></template>
							      <span>t.ex. elsparkcykel, skateboard etc."</span>
							    </v-tooltip>
							</div>
							<div class="survey-thirds-middle"><vue-slider dotSize="20" :max="200" v-model="weeklyMisc"></vue-slider></div>
							<div class="survey-thirds-last"><v-text-field placeholder="0" v-model="weeklyMisc"></v-text-field> km</div>
						</div>

						<div class="survey-thirds-sum"><strong>SUMMA</strong> <span>{{ weeklySum }} km</span></div>

						<div class="survey-sum mt-6">
							Uppgifterna du angett ovan motsvarar <strong>{{ yearlyDistance }} km</strong> utslaget på ett år.
							<br/>
							<br/>
							<em>Om reslängden per vecka verkar rimlig, vänligen gå vidare i enkäten. Om inte, justera i frågorna ovan.</em></div>


						<div v-if="weeklyCar">
							<h4 class="mt-5">Hur är bilresorna fördelade utifrån drivmedel?</h4>
							<p class="mb-2"><em class="smaller-em">(Summan för de olika drivmedlen måste matcha med Bil - själv eller med hushållsmedlem ovan <strong>({{ weeklyCar }} km)</strong>. Du kan antingen dra i ikonen eller skriva in avståndet direkt i kolumnen till höger.)</em></p>

							<div class="survey-thirds electricity mt-3">
								<div class="survey-thirds-first-alt" :class="{surveyError: errors.includes('weeklyElectricity')}">El</div>
								<div class="survey-thirds-middle"><vue-slider dotSize="20" :max="1000" v-model="weeklyElectricity"></vue-slider></div>
								<div class="survey-thirds-last"><v-text-field placeholder="0" v-model="weeklyElectricity"></v-text-field> km</div>
							</div>

							<div class="survey-thirds hybrid mt-3">
								<div class="survey-thirds-first-alt" :class="{surveyError: errors.includes('weeklyHybrid')}">Hybrid/gas</div>
								<div class="survey-thirds-middle"><vue-slider dotSize="20" :max="1000" v-model="weeklyHybrid"></vue-slider></div>
								<div class="survey-thirds-last"><v-text-field placeholder="0" v-model="weeklyHybrid"></v-text-field> km</div>
							</div>

							<div class="survey-thirds gas mt-3">
								<div class="survey-thirds-first-alt" :class="{surveyError: errors.includes('weeklyGas')}">Bensin/diesel<br/>/etanol</div>
								<div class="survey-thirds-middle"><vue-slider dotSize="20" :max="1000" v-model="weeklyGas"></vue-slider></div>
								<div class="survey-thirds-last"><v-text-field placeholder="0" v-model="weeklyGas"></v-text-field> km</div>
							</div>
						</div>
					</template>

					<div class="nudgd-btn inline survey-submit mt-10" @click="nextStep()">Gå vidare</div>
					<div style="clear:both;"></div>
				</template>
			</div>

			<div v-if="stepIndex === 1">
				<h4 class="mt-6 mb-2" :class="{surveyError: errors.includes('reasonForTransport')}">I valet av färdsätt till och från skolan väger detta tyngst</h4>
				<p class="mb-2"><em class="smaller-em">Ange ett eller två alternativ</em></p>

				<div class="chip-rows">
					<v-chip-group active-class="primary--text" column multiple v-model="reasonForTransport" max="2">
					<v-chip value="Att resa så som kollegor/vänner/andra föräldrar gör" outlined>Att resa så som kollegor/vänner/andra föräldrar gör</v-chip>
					<v-chip value="Bekvämlighet" outlined>Bekvämlighet</v-chip>
					<v-chip value="Hälsa" outlined>Hälsa</v-chip>
					<v-chip value="Kostnad" outlined>Kostnad</v-chip>
				    <v-chip value="Miljö" outlined>Miljö</v-chip>
					<v-chip value="Säkerhet" outlined>Säkerhet</v-chip>
				    <v-chip value="Tid" outlined>Tid</v-chip>
				</v-chip-group>
				</div>

				<h4 class="mt-5 mb-2">Kommentar</h4>
				<v-textarea class="mt-4" rows="4" hide-details solo v-model="reasonForTransportComment" placeholder="Lägg gärna till något om vad som påverkar hur du tänker med resor till och från skolan framöver, och varför – vi vill höra vad du tycker!"></v-textarea>

				<div class="nudgd-btn inline survey-submit mt-10" @click="nextStep()">Gå vidare</div>
				<div class="nudgd-btn small survey-submit mt-10" @click="prevStep()">Tillbaka</div>
				<div style="clear:both;"></div>
			</div>

			<div v-if="stepIndex === 2">
				<div v-if=" ! isStaff">
					<h4 class="mb-2" :class="{surveyError: errors.includes('worksInSchool')}">Jobbar du på en grundskola i kommunen (antingen kommunal eller privat)?</h4>
					<v-chip-group active-class="primary--text" column v-model="worksInSchool">
						<v-chip value="Ja" outlined>Ja</v-chip>
						<v-chip value="Nej" outlined>Nej</v-chip>
					</v-chip-group>
				</div>

				<div v-if="isStaff">
					<h4 class="mb-2" :class="{surveyError: errors.includes('hasChildrenInSchool')}">Har du barn som går på en av Helsingborgs stads grundskolor (antingen kommunal eller privat)?</h4>
					<v-chip-group active-class="primary--text" column v-model="hasChildrenInSchool">
						<v-chip value="Ja" outlined>Ja</v-chip>
						<v-chip value="Nej" outlined>Nej</v-chip>
					</v-chip-group>
				</div>

				<h4 class="mb-2" :class="{surveyError: errors.includes('email')}">{{ $processText('{email-address-input}') }} </h4>
				<p class="mb-2"><em class="smaller-em">OBS! Denna uppgift är <strong>väldigt värdefull för projektet</strong>, så vi hade verkligen uppskattat om du kan tänka dig att dela den med oss. Vi använder den både för smidigare kommunikation med dig, om vi vill fråga något mer framöver (i princip ett kort kompletterande mail den närmaste veckan och ett-två därefter, senare i höst).<br>
<!-- Om du lämnar din epost deltar du även i en <strong>utlottning med priset att två klasser får komma till Habiteum för lyxfika, kul tipspromenad och samskapande – allt med cykeltema!</strong> --> 
					Flera klasser eller inga barn i grundskoleålder? Då får du välja vilken klass du vill!</em></p>

				<v-text-field dense hide-details placeholder="E-post" v-model="email" outlined></v-text-field>

				<br>
				Enkäten och dina personuppgifter hanteras av företaget Nudgd på uppdrag av Helsingborgs stad. Läs mer om <a :href="$processText('https://data.nudgd.se/{slug}')" target="_blank">hur dina personuppgifter hanteras.</a>. Lämnar du din mailadress här är dina svar tillfälligt inte längre anonyma, men vi kommer att radera den från din svarsdata inom 90 dagar.

				<div class="mt-5">
					<div style="display: inline-block;" class="nudgd-btn survey-submit mt-3" @click="submitResponse(true)">Skicka in och acceptera cookies</div>
					<div class="nudgd-btn small survey-submit mt-3" @click="submitResponse()">Skicka in och avböj cookies</div>
					<p class="mt-2"><em class="smaller-em"><a href="https://nudgd.se/om-nudgd/integritetspolicy/" target="_blank">Hur vi använder cookies och varför</a>. inte minst för att minimera risken att du behöver göra denna enkät igen när du går in nästa gång!</em></p>
				</div>

				<div class="nudgd-btn small survey-submit mt-0" @click="prevStep()">Tillbaka</div>

				<div style="clear:both;"></div>
			</div>

			<v-progress-linear color="#00A19A" height="10" rounded class="mt-8" :value="(stepIndex + 1) / steps * 100"></v-progress-linear>

			<br><br>
			<p>Frågor? Helsingborgs stads <a href="https://helsingborg.se/kommun-och-politik/kontakt-och-paverkan/kontakta-kommunen/" target="_blank">kontaktcenter</a> finns här för dig.</p>
			<br>

			<div style="display:flex;">
				<a style="width:30%; margin-right:5%;" target="_blank" href="https://raptorproject.eu/"><img style="max-width:100%;" id="raptor-logo" class="hbg-footer-logo" src="https://www.dropbox.com/scl/fi/utuohojig7em00x34xhti/Raptor-logos_Blue_Horizontal.png?rlkey=7uhpzldlhb4b1f1ah0utvf94e&raw=1" /></a>
            	<a style="width:30%; margin-right:5%;" target="_blank" href="https://www.eiturbanmobility.eu/"><img style="max-width:100%;" id="eit-urban-mobility-logo" class="hbg-footer-logo" src="https://www.dropbox.com/scl/fi/yj3v6c6dg2pos2txuwc7i/eit-urban-mobility-logo.png?rlkey=aw7ja0iwut7koyauhbxs0hckx&raw=1" /></a>
            	<a style="width:30%;" target="_blank" href="https://www.eiturbanmobility.eu/"><img style="max-width:100%;" id="co-funded-by-the-eu-logo" class="hbg-footer-logo" src="https://www.dropbox.com/scl/fi/f200n29souasqkwzwzfht/logo-co-funded-eu.jpeg?rlkey=wczt3nyt5sizf93f2dhjo1cqo&raw=1" /></a>
			</div>

			<p class="mt-2 mb-0"><em class="smaller-em">
				The project is supported by EIT Urban Mobility, an initiative of the European Institute of Innovation and Technology (EIT), a body of the European Union. EIT Urban Mobility acts to accelerate prositive change on mobility to make urban spaces more liveable. Learn more: eiturbanmobility.eu
			</em></p>
		</div>
	</div>
</template>

<script>
import axios from 'axios'
import VueSlider from 'vue-slider-component'
import 'vue-slider-component/theme/default.css'
import ModeOfTransport from '@/components/ModeOfTransport.vue'

export default {
	components: {VueSlider, ModeOfTransport},

	mounted() {	
		this.uuid = Math.random().toString(36).substr(2, 5)
		this.$store.state.surveyId = 10

		// For testing purposes
		if (this.$route.query.page) {
			this.stepIndex = parseInt(this.$route.query.page)
		}

		// Try to get survey response
		axios.get(this.$apiUrl + '/api/survey/1/response/' + this.slug).then(res => {	
			const data = {...res.data.response, uuid: res.data.uuid}

			for (const prop in data) {
				this[prop] = data[prop]
			}
		})

		// Get end user
		axios.get(this.$apiUrl + '/api/end-user/' + this.slug + '?s=Pre-Survey').then(res => {
			this.$store.state.endUser = res.data.endUser
			this.$store.state.client = res.data.endUser.client
			this.$store.state.translations = res.data.translations
		})

		if ( ! this.endUser.test_user && this.$getCookie('raptorCookie') && ! this.$route.query.fnp) {
			this.$router.push({name: 'EndUser', params: {slug: this.$getCookie('raptorCookie')}})
			return
		}
	},

	data() {
		return {
			steps: 3,

			mainModeOfTransport: null,
			numberOfTrips: null,

			frequencyWalk: null,
			frequencyBike: null,
			frequencyTransit: null,
			frequencyCar: null,
			frequencyCarCoride: null,
			frequencyMisc: null,

			weeklyWalking: null,
			weeklyBike: null,
			weeklyTransit: null,
			weeklyCar: null,
			weeklyCarCoride: null,
			weeklyMisc: null,

			weeklyElectricity: null,
			weeklyHybrid: null,
			weeklyGas: null,

			reasonForTransport: null,
			reasonForTransportComment: null,
			worksInSchool: null,
			hasChildrenInSchool: null,

			email: null,

			firstStepConfirmed: false,
			errors: [],
			engaged: [],
			stepIndex: 0,
			thankYou: false,

			// Each array represents the required fields of a step
			required: [
				[
					'mainModeOfTransport', 'frequencyWalk', 'frequencyBike',
					'frequencyTransit', 'frequencyCar', 'frequencyCarCoride', 'frequencyMisc',
				],
				[
					'reasonForTransport'
				],
				[
					
				],
			],
		}
	},

	watch: {
		numberOfTrips(val) { this.engaged.push('numberOfTrips') },
		mainModeOfTransport(val) { this.engaged.push('mainModeOfTransport') },
		frequencyWalk(val) { this.engaged.push('frequencyWalk') },
		frequencyBike(val) { this.engaged.push('frequencyBike') },
		frequencyTransit(val) { this.engaged.push('frequencyTransit') },
		frequencyCar(val) { this.engaged.push('frequencyCar') },
		frequencyCarCoride(val) { this.engaged.push('frequencyCarCoride') },
		frequencyMisc(val) { this.engaged.push('frequencyMisc') },

		weeklyWalking(val) { this.engaged.push('weeklyWalking') },
		weeklyBike(val) { this.engaged.push('weeklyBike') },
		weeklyTransit(val) { this.engaged.push('weeklyTransit') },
		weeklyCar(val) { this.engaged.push('weeklyCar') },
		weeklyCarCoride(val) { this.engaged.push('weeklyCarCoride') },
		weeklyMisc(val) { this.engaged.push('weeklyMisc') },
		weeklyElectricity(val) { this.engaged.push('weeklyElectricity') },
		weeklyGas(val) { this.engaged.push('weeklyGas') },
		weeklyHybrid(val) { this.engaged.push('weeklyHybrid') },

		reasonForTransport(val) { this.engaged.push('reasonForTransport') },
		worksInSchool(val) { this.engaged.push('worksInSchool') },
		hasChildrenInSchool(val) { this.engaged.push('hasChildrenInSchool') },
	},

	methods: {
		selectFrequency(property, value) {
			this[property] = value
		},

		selectModeOfTransport(mode) {
			this.mainModeOfTransport = mode
		},

		confirmFirstStep() {
			if ( ! this.mainModeOfTransport) {
				alert('Var god och välj ditt huvudsakliga färdsätt')
				return
			}

			this.firstStepConfirmed = true
		},

		nextStep() {
			if ( ! this.validate()) {
				alert('Var god och fyll i alla rödmarkerade fält')
				return
			}

			window.scrollTo(0, 0)
			this.stepIndex++
			this.saveState()
		},

		validate() {
			this.errors = []

			let required = [...this.required[this.stepIndex]]

			if (this.stepIndex === 0) {
				if (parseInt(this.frequencyWalk) > 0) { required.push('weeklyWalking') }
				if (parseInt(this.frequencyBike) > 0) { required.push('weeklyBike') }
				if (parseInt(this.frequencyTransit) > 0) { required.push('weeklyTransit') }
				if (parseInt(this.frequencyCar) > 0) { required.push('weeklyCar') }
				if (parseInt(this.frequencyCarCoride) > 0) { required.push('weeklyCarCoride') }
				if (parseInt(this.frequencyMisc) > 0) { required.push('weeklyMisc') }

				if (this.frequencyMisc || this.frequencyBike || this.frequencyTransit || this.frequencyCar || this.frequencyCarCoride || this.frequencyMisc) {
					required.push('numberOfTrips')
				}
			}

			if (this.stepIndex === 2) {
				if (this.isStaff) {
					required.push('hasChildrenInSchool')
				} else {
					required.push('worksInSchool')
				}
			}

			this.errors = required.filter(key => {
				return ! this.engaged.includes(key) || (this.$data[key] instanceof Array && this.$data[key].length === 0)
			})

			if (this.stepIndex === 0) {
				// If weeklyCar > 0, then the  type of fuels should add up
				if (this.weeklyCar > 0 && (parseInt(this.weeklyElectricity || 0) + parseInt(this.weeklyGas || 0) + parseInt(this.weeklyHybrid || 0) != this.weeklyCar)) {
					alert('Summan av drivmedel måste bli ' + this.weeklyCar)
					this.errors.push('weeklyElectricity')
					this.errors.push('weeklyGas')
					this.errors.push('weeklyHybrid')
				}
			}

			this.errors =  this.errors.concat(required.filter(val => ! this.engaged.includes(val)))

			return this.errors.length === 0
		},

		prevStep() {
			window.scrollTo(0, 0)
			this.stepIndex--
		},

		saveState() {
			axios.post(this.$apiUrl + '/api/survey/1', {
				...this.$data,
				uuid: this.uuid,
				slug: this.slug,
				seconds_passed: performance ? parseInt(performance.now() / 1000) : null,
				completed_pages: this.stepIndex + 1
			})
		},

		submitResponse(withCookie) {
			if ( ! this.validate()) {
				alert('Var god och fyll i alla rödmarkerade fält')
				return
			}

			if (withCookie) {
				this.$setCookie('raptorCookie', this.endUser.slug);
			}

			axios.post(this.$apiUrl + '/api/survey/1', {
				...this.$data,
				uuid: this.uuid,
				slug: this.slug,
				seconds_passed: performance ? parseInt(performance.now() / 1000) : null,
				completed_pages: this.stepIndex + 1,
				complete: true
			}).then(res => {
				this.$router.push({name: 'EndUser', params: {slug: this.endUser.slug}})
            })
		}
	},

	computed: {
		isStaff() {
			return this.endUser?.tags?.find(t => t.id == 1347);
		},
		
		slug() {
			// Cookie slug precedence
			return this.$route.params.slug || this.$getCookie('openEndUser')
		},

		endUser() {
			return this.$store.state.endUser
		},

		weeklySum() {
			return parseInt(this.weeklyWalking || 0) + parseInt(this.weeklyBike || 0) + parseInt(this.weeklyTransit || 0) + parseInt(this.weeklyCar || 0) + parseInt(this.weeklyCarCoride || 0) + parseInt(this.weeklyMisc || 0)
		},

		computedWeeklyAverage() {
			if ( ! this.numberOfTrips) {
				return 0
			}

			return (this.weeklySum / this.numberOfTrips).toFixed(0).replace('.', ',')
		},

		yearlyDistance() {
			return this.weeklySum * 52
		}
	}
}
</script>
